import { useEffect, useState } from 'react'

// `usePageSettled` exposes a single global "the page has finished its
// initial work" signal. It starts as `false` and flips to `true` once the
// browser has fired `load` AND we've had a chance to run an idle callback.
//
// We use this to defer expensive UI (e.g. NumberFlow web components, entry
// animations) until after the LCP / first paint window so they don't block
// the critical render path. After it settles, components are free to
// upgrade themselves to the fancy version (animated counters, etc).
//
// This is intentionally a module-level singleton so it only fires once per
// page load and every consumer shares the same answer.

let settled = false
const listeners = new Set()

const notify = () => {
  for (const cb of listeners) cb()
}

const markSettled = () => {
  if (settled) return
  settled = true
  notify()
}

if (typeof window !== 'undefined') {
  const onReady = () => {
    const idle =
      typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback
        : cb => setTimeout(cb, 600)
    idle(markSettled, { timeout: 2500 })
  }

  if (document.readyState === 'complete') {
    onReady()
  } else {
    window.addEventListener('load', onReady, { once: true })
  }
}

const usePageSettled = () => {
  // SSR: `settled` is `false` (module top-level didn't touch window). The
  // client hydrates with the same `false`, so no hydration mismatch.
  const [isSettled, setIsSettled] = useState(settled)

  useEffect(() => {
    if (settled) {
      if (!isSettled) setIsSettled(true)
      return
    }
    const cb = () => setIsSettled(true)
    listeners.add(cb)
    return () => {
      listeners.delete(cb)
    }
  }, [isSettled])

  return isSettled
}

export default usePageSettled
